// ─────────────────────────────────────────────
// Oike Acompañamiento — keys, labels, questionnaire and starter programs.
//
// Shared by the coach panel (src/components/coaching/), the participant space
// (src/portal/) and the server (netlify/functions/coaching-*.mts). Values stored
// in Airtable are the English keys; the Spanish labels live here.
//
// Two separate decisions for every process:
// - Modality: individual or team (a team process always includes individual sessions).
// - Focus: personal, professional or combined. Agreed at the start and recorded
//   with the goals; it only changes with a new explicit agreement, never by the AI.
// Sales and business development are specialties added when the client asks for
// them: they are never the default of a program, a questionnaire or a proposal.
// ─────────────────────────────────────────────

export const COACHING_MODALITIES = [
  { key: 'individual', label: 'Individual' },
  { key: 'team', label: 'Equipo' },
];

export const COACHING_FOCUS = [
  { key: 'personal', label: 'Personal' },
  { key: 'professional', label: 'Profesional' },
  { key: 'combined', label: 'Personal y profesional' },
];

/** Specialties: only when the client asks for them. */
export const COACHING_SPECIALTIES = [
  { key: 'business', label: 'Desarrollo de negocios' },
  { key: 'sales', label: 'Ventas' },
];

/** Library areas each focus can use (specialties add their own area). */
export const FOCUS_AREAS = {
  personal: ['personal'],
  professional: ['professional'],
  combined: ['personal', 'professional'],
};

/** The library areas a process may use: its agreed focus plus the specialties it asked for. */
export function allowedAreas(focus, specialties = []) {
  return [...new Set([...(FOCUS_AREAS[focus] || []), ...specialties.filter(s => COACHING_SPECIALTIES.some(x => x.key === s))])];
}

export const PROCESS_STATUSES = [
  { key: 'invited', label: 'Invitación enviada' },
  { key: 'questionnaire', label: 'Cuestionario' },
  { key: 'agreement', label: 'Acuerdo inicial' },
  { key: 'active', label: 'En curso' },
  { key: 'closed', label: 'Cerrado' },
];

export const SESSION_KINDS = [
  { key: 'individual', label: 'Individual' },
  { key: 'group', label: 'Grupal' },
];

export const SESSION_STATUSES = [
  { key: 'unscheduled', label: 'Sin fecha' },
  { key: 'scheduled', label: 'Agendada' },
  { key: 'done', label: 'Realizada' },
  { key: 'cancelled', label: 'Cancelada' },
];

export const ASSIGNMENT_KINDS = [
  { key: 'exercise', label: 'Ejercicio' },
  { key: 'material', label: 'Material' },
  { key: 'reflection_prompt', label: 'Pregunta para reflexionar' },
];

export const ENTRY_KINDS = [
  { key: 'reflection', label: 'Reflexión' },
  { key: 'progress', label: 'Avance' },
  { key: 'exercise_response', label: 'Respuesta a una actividad' },
];

/**
 * Who can see each kind of content. The server applies it; the screens show it
 * with the same labels so nobody has to guess.
 */
export const VISIBILITY = {
  coach_private: { label: 'Solo la coach', hint: 'Notas privadas de la coach. No las ve el participante, ni el equipo, ni quien contrata.' },
  participant_private: { label: 'Solo tú', hint: 'Reflexión privada. No la ve nadie más, tampoco la coach, hasta que decidas compartirla.' },
  shared: { label: 'Tú y la coach', hint: 'Lo ven solo la persona y la coach.' },
  group: { label: 'Equipo', hint: 'Lo ven la coach y las personas del equipo. Quien contrata no ve el contenido individual.' },
  sponsor: { label: 'Quien contrata', hint: 'Solo datos grupales: nunca avances individuales, objetivos personales, notas, reflexiones, transcripciones ni mensajes.' },
};

/** Groups smaller than this never get individual-derived aggregates (same rule as the Diagnostic). */
export const MIN_GROUP_FOR_AGGREGATES = 3;

export const COACHING_TEXT_LIMITS = {
  name: 120,
  description: 2000,
  goal: 400,
  note: 8000,
  summary: 6000,
  reflection: 6000,
  message: 4000,
  instructions: 3000,
  link: 500,
  commitment: 400,
  answer: 3000,
};
export const MAX_GOALS = 8;
export const MAX_SEQUENCE = 40;

/** Transcripts: pasted as text with recorded consent; never automatic recording. */
export const TRANSCRIPT_DEFAULTS = {
  retentionDays: 60,      // per process, configurable (0 = delete when the process closes)
  maxRetentionDays: 365,
  maxCharacters: 200000,
};

/** Booking defaults (the coach changes them in Agenda). */
export const AVAILABILITY_DEFAULTS = {
  timeZone: 'America/Argentina/Buenos_Aires',
  weekly: { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 0: [] },
  blocked: [],
  sessionMinutes: 60,
  bufferMinutes: 15,
  minNoticeHours: 24,
  horizonDays: 28,
  slotStepMinutes: 30,
};
export const WEEKDAYS = [
  { key: 1, label: 'Lunes' }, { key: 2, label: 'Martes' }, { key: 3, label: 'Miércoles' }, { key: 4, label: 'Jueves' },
  { key: 5, label: 'Viernes' }, { key: 6, label: 'Sábado' }, { key: 0, label: 'Domingo' },
];

/** External connections: prepared, not active until their credentials exist. */
export const INTEGRATIONS = [
  { key: 'calendar', label: 'Google Calendar', env: ['COACHING_GOOGLE_CALENDAR_ID'], doc: 'docs/coaching/configuracion.md#calendario' },
  { key: 'email', label: 'Avisos por correo', env: ['COACHING_EMAIL_FROM'], doc: 'docs/coaching/configuracion.md#avisos-por-correo' },
];

// ── Questionnaire (before the first session) ──
// Adapted to the focus: commercial questions appear only with the sales or
// business specialty. Team processes add the team block.

const Q = (key, label, opts = {}) => ({ key, label, type: 'text', ...opts });

export const QUESTIONNAIRE_VERSION = '1';

const QUESTIONS = [
  // Everyone
  Q('context', '¿Qué está pasando hoy en tu vida o en tu trabajo que te trae a este proceso?', { required: true }),
  Q('expectations', '¿Qué te gustaría que fuera distinto al terminar el proceso?', { required: true }),
  Q('strengths', '¿Qué cosas te salen bien, aunque no siempre las valores?'),
  Q('challenges', '¿Qué te está costando más en este momento?'),
  Q('support', '¿Qué te ayudó en otros momentos en los que quisiste cambiar algo?'),
  // Personal
  Q('values', '¿Qué es importante para ti y no quieres negociar?', { focus: ['personal', 'combined'] }),
  Q('habits', '¿Qué hábito o rutina te gustaría construir o dejar?', { focus: ['personal', 'combined'] }),
  Q('energy', '¿Qué te da energía y qué te la quita en una semana normal?', { focus: ['personal', 'combined'] }),
  // Professional
  Q('role', '¿Cuál es tu rol o actividad profesional hoy?', { focus: ['professional', 'combined'] }),
  Q('workChallenge', '¿Qué situación de trabajo te gustaría manejar mejor (conversaciones, límites, decisiones, prioridades)?', { focus: ['professional', 'combined'] }),
  Q('growth', '¿Hacia dónde te gustaría crecer profesionalmente en el próximo año?', { focus: ['professional', 'combined'] }),
  // Combined
  Q('balance', '¿Cómo se cruzan hoy lo personal y lo profesional para ti?', { focus: ['combined'] }),
  // Team
  Q('teamExpectations', '¿Qué esperas del trabajo con tu equipo en este proceso?', { modality: ['team'] }),
  Q('teamContribution', '¿Qué crees que aportas al equipo?', { modality: ['team'] }),
  // Specialties (only when requested)
  Q('salesContext', '¿Cómo es hoy tu trabajo comercial: qué vendes, a quién y qué parte te cuesta más?', { specialty: ['sales'] }),
  Q('businessContext', '¿Qué parte del desarrollo de tu negocio quieres trabajar (clientes, propuesta, alianzas, crecimiento)?', { specialty: ['business'] }),
  // Closing
  Q('availability', '¿Hay algo sobre horarios o formas de trabajo que convenga saber?'),
  Q('anythingElse', '¿Algo más que quieras que sepa antes de la primera sesión?'),
];

/** The questions for one process: by agreed (or proposed) focus, modality and specialties. */
export function questionnaireFor({ focus, modality, specialties = [] }) {
  return QUESTIONS.filter(q =>
    (!q.focus || q.focus.includes(focus)) &&
    (!q.modality || q.modality.includes(modality)) &&
    (!q.specialty || q.specialty.some(s => specialties.includes(s))));
}

export const QUESTIONNAIRE_INTRO = 'Estas preguntas me ayudan a preparar la primera sesión. No hay respuestas correctas y puedes dejar en blanco lo que prefieras conversar en persona. Tus respuestas las vemos solo tú y yo.';

// ── Starter programs ──
// Editable proposals, clearly marked as such. They describe a structure, not a
// methodology of their own, and they promise no results. Ale adapts them.

const seq = (items) => items.map((x, i) => ({ n: i + 1, kind: x[0], title: x[1], purpose: x[2], tools: [], between: x[3] ? [x[3]] : [] }));

export const STARTER_PROGRAMS = [
  {
    starterKey: 'individual_personal_v1',
    name: 'Recorrido individual · foco personal (propuesta editable)',
    description: 'Propuesta editable para un proceso individual centrado en lo personal: valores, hábitos, límites o confianza, según lo que se acuerde en la primera sesión.',
    modality: 'individual', focus: 'personal', specialties: [],
    durationWeeks: 12, sessions: 8, groupSessions: 0, individualSessions: 8, sessionMinutes: 60,
    goals: ['Definir con claridad qué quiere cambiar la persona y por qué le importa', 'Identificar recursos y obstáculos propios', 'Sostener acciones concretas entre sesiones'],
    sequence: seq([
      ['individual', 'Encuadre y acuerdo', 'Revisar el cuestionario, acordar foco, objetivos y forma de trabajo.', 'Escribir en una frase qué te gustaría que fuera distinto en 3 meses.'],
      ['individual', 'Punto de partida', 'Explorar la situación actual y lo que ya funciona.', 'Registrar durante una semana un momento que te dio energía y uno que te la quitó.'],
      ['individual', 'Valores y prioridades', 'Conectar los objetivos con lo que es importante para la persona.', null],
      ['individual', 'Patrones y alternativas', 'Mirar patrones de comportamiento y ensayar alternativas.', 'Probar una alternativa pequeña y anotar qué pasó.'],
      ['individual', 'Revisión intermedia', 'Revisar avances sobre los objetivos acordados y ajustar.', null],
      ['individual', 'Hábitos y límites', 'Diseñar acciones sostenibles.', 'Sostener una acción concreta durante dos semanas.'],
      ['individual', 'Consolidación', 'Revisar lo aprendido y lo que todavía cuesta.', null],
      ['individual', 'Cierre', 'Revisar objetivos, reconocer avances y definir cómo seguir sin acompañamiento.', null],
    ]),
    activities: ['Reflexiones breves después de cada sesión', 'Acciones pequeñas acordadas entre sesiones'],
    review: 'Revisión intermedia en la sesión 5 y revisión final en la última sesión, siempre sobre los objetivos acordados y en conversación con la persona.',
    closing: 'Sesión de cierre: qué cambió, qué quedó pendiente y qué acciones sostiene la persona por su cuenta.',
  },
  {
    starterKey: 'individual_professional_v1',
    name: 'Recorrido individual · foco profesional (propuesta editable)',
    description: 'Propuesta editable para un proceso individual centrado en lo profesional: comunicación, liderazgo de sí, decisiones, prioridades o relaciones de trabajo. Sin contenido de ventas salvo que se agregue como especialidad.',
    modality: 'individual', focus: 'professional', specialties: [],
    durationWeeks: 8, sessions: 6, groupSessions: 0, individualSessions: 6, sessionMinutes: 60,
    goals: ['Acordar uno o dos objetivos profesionales concretos', 'Practicar conversaciones o situaciones de trabajo relevantes', 'Sostener compromisos entre sesiones'],
    sequence: seq([
      ['individual', 'Encuadre y acuerdo', 'Revisar el cuestionario, acordar foco, objetivos y forma de trabajo.', null],
      ['individual', 'Situación actual', 'Explorar el contexto de trabajo, fortalezas y desafíos.', 'Observar una situación de trabajo que quieras manejar distinto.'],
      ['individual', 'Comunicación y relaciones', 'Trabajar una conversación o relación concreta.', 'Preparar y tener la conversación acordada.'],
      ['individual', 'Revisión intermedia', 'Revisar avances y ajustar objetivos si hace falta, con acuerdo explícito.', null],
      ['individual', 'Prioridades y decisiones', 'Ordenar prioridades y criterios de decisión.', null],
      ['individual', 'Cierre', 'Revisar objetivos y definir próximos pasos.', null],
    ]),
    activities: ['Observación de situaciones de trabajo', 'Preparación de conversaciones'],
    review: 'Revisión intermedia en la sesión 4 y revisión final en la sesión 6.',
    closing: 'Sesión de cierre con revisión de objetivos y plan de continuidad.',
  },
  {
    starterKey: 'individual_combined_v1',
    name: 'Recorrido combinado personal y profesional (propuesta editable)',
    description: 'Propuesta editable para un proceso individual que trabaja lo personal y lo profesional, cuando la persona quiere mirar cómo se cruzan.',
    modality: 'individual', focus: 'combined', specialties: [],
    durationWeeks: 16, sessions: 10, groupSessions: 0, individualSessions: 10, sessionMinutes: 60,
    goals: ['Acordar objetivos en lo personal y en lo profesional', 'Entender cómo se influyen entre sí', 'Sostener acciones en ambos planos'],
    sequence: seq([
      ['individual', 'Encuadre y acuerdo', 'Revisar el cuestionario y acordar foco y objetivos en ambos planos.', null],
      ['individual', 'Mapa de la situación', 'Explorar lo personal y lo profesional y cómo se cruzan.', 'Registrar qué te da y qué te quita energía en la semana.'],
      ['individual', 'Valores y propósito', 'Conectar objetivos con lo que es importante para la persona.', null],
      ['individual', 'Objetivo profesional', 'Trabajar el primer objetivo profesional.', 'Una acción concreta en el trabajo.'],
      ['individual', 'Objetivo personal', 'Trabajar el primer objetivo personal.', 'Una acción concreta fuera del trabajo.'],
      ['individual', 'Revisión intermedia', 'Revisar avances y ajustar, con acuerdo explícito si cambia algo.', null],
      ['individual', 'Límites y equilibrio', 'Trabajar límites entre ambos planos.', null],
      ['individual', 'Comunicación', 'Trabajar conversaciones relevantes en cualquiera de los dos planos.', null],
      ['individual', 'Consolidación', 'Revisar lo aprendido y lo pendiente.', null],
      ['individual', 'Cierre', 'Revisar objetivos y definir cómo seguir.', null],
    ]),
    activities: ['Reflexiones breves', 'Acciones acordadas en ambos planos'],
    review: 'Revisión intermedia en la sesión 6 y final en la sesión 10.',
    closing: 'Sesión de cierre con revisión de ambos planos.',
  },
  {
    starterKey: 'team_v1',
    name: 'Equipo · acuerdos y comunicación (propuesta editable)',
    description: 'Propuesta editable para un equipo: sesiones grupales para acuerdos y comunicación, más sesiones individuales con cada integrante. Lo individual queda entre cada persona y la coach.',
    modality: 'team', focus: 'professional', specialties: [],
    durationWeeks: 10, sessions: 6, groupSessions: 4, individualSessions: 2, sessionMinutes: 90,
    goals: ['Acordar objetivos compartidos del equipo', 'Mejorar acuerdos de trabajo y comunicación', 'Dar espacio individual a cada integrante'],
    sequence: seq([
      ['group', 'Encuadre del equipo', 'Presentar el proceso, acordar foco y objetivos compartidos y reglas de confidencialidad.', null],
      ['individual', 'Sesión individual 1', 'Objetivos propios de cada integrante dentro del proceso del equipo.', null],
      ['group', 'Comunicación y acuerdos', 'Trabajar cómo se comunica el equipo y qué acuerdos necesita.', 'Probar uno de los acuerdos durante dos semanas.'],
      ['group', 'Revisión intermedia del equipo', 'Revisar los acuerdos y ajustar.', null],
      ['individual', 'Sesión individual 2', 'Revisar avances propios.', null],
      ['group', 'Cierre del equipo', 'Revisar objetivos compartidos y definir cómo sostener los acuerdos.', null],
    ]),
    activities: ['Acuerdos del equipo para probar entre sesiones', 'Reflexión individual opcional'],
    review: 'Revisión intermedia grupal en la sesión 4; los avances individuales se revisan solo en las sesiones individuales.',
    closing: 'Cierre grupal sobre los objetivos compartidos. Quien contrata recibe solo información grupal.',
  },
];

export const STARTER_NOTE = 'Propuesta editable de Oike: una estructura de partida para adaptar. No es una metodología propia ni promete resultados.';

/** Program content as it is stored (Content JSON) and copied into each process. */
export function programContent(p) {
  return {
    goals: (p.goals || []).slice(0, MAX_GOALS),
    sequence: (p.sequence || []).slice(0, MAX_SEQUENCE),
    activities: p.activities || [],
    tools: p.tools || [],
    review: p.review || '',
    closing: p.closing || '',
  };
}

/** Scope of the coaching: shown once in the coach panel (never on every screen). */
export const SCOPE_NOTE = 'El acompañamiento trabaja valores, identidad, confianza, hábitos, límites, comunicación, relaciones y patrones de comportamiento. Los temas de depresión y las necesidades psicológicas profundas se derivan a profesionales habilitados. La IA no diagnostica ni propone tratamientos.';

export const label = (list, key) => list.find(x => x.key === key)?.label || key || '—';
